import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import {
	DetailedCellProps,
	onRowLeave,
	useRoutesMonitoringTranslations,
	ViewItemButton
} from '@bringg-frontend/monitoring-shared';
import { GroupData, Run } from '@bringg/types';
import { useStores } from 'bringg-web';

import PlanningTag from './components/planning-tag/planning-tag';
import ActionsMenu from './components/actions-menu/actions-menu';
import useRouteMonitoringStore from '../../../stores/helpers/use-route-monitoring-store';
import { etaCalculatorProvider } from '../../../../shared/utils/eta-calculator/eta-calculator-provider';
import { View } from '../../../../shared/types';
import useIsRowHover from 'bringg-web/features/monitoring/shared/hooks/use-is-row-hover';
import useActionItems from 'bringg-web/features/monitoring/shared/hooks/use-action-items';
import VehicleAssignerTool from '../../../components/vehicle-assigner-tool';
import UserAssignerTool from 'bringg-web/features/monitoring/shared/components/user-assigner-tool';
import ColorIndicator from '../../../../shared/components/color-indicator/color-indicator';
import useOrderExplorerTranslations from 'bringg-web/features/monitoring/order-explorer/use-order-explorer-translations';
import { isRouteInvalid } from '../../../utils/route-utils';
import RouteRow from 'bringg-web/features/monitoring/shared/components/row-identifier/route-row';

import styles from './route-identifier.module.scss';

const RouteIdentifier = ({ row }: DetailedCellProps<GroupData>) => {
	const { runStore } = useStores();
	const rowID = row.data.id;

	let route: Run;

	if (rowID) {
		route = runStore.get(rowID);
	}

	const {
		routesMonitoringTableViewStore: { getRouteColor, isRouteViewed, toggleViewRoute },
		routeMonitoringMapViewStore,
		routeMonitoringActionsStore: { loadingRoutes }
	} = useRouteMonitoringStore();

	const { noRoute } = useOrderExplorerTranslations();
	const { invalidRoute, invalidCalculation } = useRoutesMonitoringTranslations();

	const [hasCalculationError, setHasCalculationError] = useState(false);

	useEffect(() => {
		if (etaCalculatorProvider.isFailed(rowID) !== hasCalculationError) {
			setHasCalculationError(etaCalculatorProvider.isFailed(rowID));
		}
	}, [routeMonitoringMapViewStore.polylines]);

	const hovered = useIsRowHover(row.id);

	const { actionItems, userAssignerModalState, vehicleAssignModalState } = useActionItems(rowID, View.routes);

	if (rowID === null) {
		return <div>{noRoute}</div>;
	}

	if (!route) {
		return null;
	}

	const isViewed = isRouteViewed(route.id);
	const hasEtaError = isRouteInvalid(route.id);
	const isRouteLoading = loadingRoutes.has(route.id);
	const routeColor = getRouteColor(route.id);

	const id = route.external_id || route.id;

	return (
		<div className={styles.routeElement} data-test-id={`route-${route.id}`} key={route.id}>
			<div className={styles.routeName} data-test-id={`route-${id}-tooltip`}>
				<ColorIndicator color={routeColor} dataTestId={`dot-${route.id}`} />

				<ViewItemButton
					onClick={(_, isSelected) => toggleViewRoute(route.id, isSelected)}
					isInView={isViewed}
					item={row}
				/>

				<RouteRow routeId={route.id} />
				{(hasEtaError || hasCalculationError) && (
					<Tooltip title={hasEtaError ? invalidRoute : invalidCalculation}>
						<BringgIcon
							iconName={BringgFontIcons.Warning}
							className={styles.errorIcon}
							data-test-id={`route-error-icon-${route.id}`}
						/>
					</Tooltip>
				)}
				{!route.ready_to_execute && <PlanningTag routeId={route.id} />}
				{isRouteLoading && <BringgIcon iconName={BringgFontIcons.Loader} className={styles.spinner} spin />}
				<div className={styles.actionMenu}>
					{actionItems.length > 0 && !isRouteLoading && hovered && (
						<ActionsMenu
							items={actionItems}
							onSelect={() => {
								onRowLeave(route.id);
							}}
						/>
					)}
				</div>
				{userAssignerModalState.open && (
					<UserAssignerTool closeModal={() => userAssignerModalState.set(false)} routeId={route.id} />
				)}
				{vehicleAssignModalState.open && (
					<VehicleAssignerTool routeId={route.id} onCloseModal={() => vehicleAssignModalState.set(false)} />
				)}
			</div>
		</div>
	);
};

export default observer(RouteIdentifier);
